// Публичные ссылки на поездку.
import { request } from '../auth/request';
import type { ApiResult } from '../auth/request';
import { appearanceToParams, type Appearance } from '../theme';
import type { AppState } from '../types';

export interface Share {
  id: string;
  createdAt: number;
  updatedAt: number;
  views: number;
}

export interface SharedTrip {
  state: AppState;
  createdAt: number;
  updatedAt: number;
  /** Название поездки на момент чтения: его владелец может поменять и потом. */
  name: string;
  /** Язык, на котором поездкой поделились. '' — ссылка создана до этого поля. */
  lang: string;
}

/** Чем ссылка представляется наружу: язык автора и описание на этом языке. */
export interface SharePreview {
  lang: string;
  summary: string;
}

/** Ссылка своя у каждой поездки: делятся маршрутом, а не всем архивом планов. */
function sharesPath(tripId?: string | null): string {
  return tripId ? `/api/shares?trip=${encodeURIComponent(tripId)}` : '/api/shares';
}

export function fetchMyShare(tripId?: string | null): Promise<ApiResult<{ share: Share | null }>> {
  return request(sharesPath(tripId));
}

/**
 * Создаёт ссылку или обновляет её содержимое текущим состоянием поездки.
 *
 * Вместе с состоянием уезжает превью: язык автора и описание поездки на этом
 * языке. Сервер их не считает — он не умеет ни считать деньги, ни переводить,
 * — а подставляет в мета-теги страницы, чтобы ссылка разворачивалась в карточку
 * ещё до того, как у читателя выполнится хоть строчка JavaScript.
 */
export function publishShare(
  tripId?: string | null,
  preview?: SharePreview,
): Promise<ApiResult<{ share: Share }>> {
  return request(sharesPath(tripId), { method: 'POST', json: preview ?? {} });
}

export function revokeShare(tripId?: string | null): Promise<ApiResult<void>> {
  return request(sharesPath(tripId), { method: 'DELETE' });
}

export function fetchSharedTrip(id: string): Promise<ApiResult<SharedTrip>> {
  return request(`/api/share/${encodeURIComponent(id)}`);
}

/**
 * Как ссылка должна открыться у читателя: язык и оформление автора.
 *
 * Это не часть поездки и не хранится на сервере — вид едет прямо в адресе.
 * Причина та же, по которой ссылку вообще шлют: человек показывает не данные,
 * а собранную им картину, и «посмотри» должно открыться тем же, что он видел
 * сам, а не тем, что угадает чужой браузер.
 */
export interface ShareView {
  lang?: string;
  appearance?: Appearance;
}

/**
 * Публичный адрес поездки с закодированным видом:
 * `/s/<id>?lang=ru&theme=darkula&fs=1.1&sp=1`.
 *
 * `lang` тут не только для интерфейса: сервер читает его же, собирая мета-теги
 * (server/seo.mjs), поэтому карточка в мессенджере приходит на языке автора
 * ещё до того, как у читателя выполнится хоть строчка JavaScript.
 */
export function shareUrl(id: string, view: ShareView = {}): string {
  const params = new URLSearchParams();
  if (view.lang) params.set('lang', view.lang);
  if (view.appearance) {
    for (const [key, value] of appearanceToParams(view.appearance)) params.set(key, value);
  }
  const query = params.toString();
  return `${window.location.origin}/s/${encodeURIComponent(id)}${query ? `?${query}` : ''}`;
}
